import { Dialog as Base } from "@base-ui/react/dialog";
import type { ComponentProps, ReactNode } from "react";

import "./surface.css";
import "./dialog.css";
import { cn } from "./cn";

type PopupProps = ComponentProps<typeof Base.Popup>;

interface DialogContentProps {
  children: ReactNode;
  className?: string;
  /** Keep the popup in the DOM (hidden) while closed. For content that must exist
   *  BEFORE the open commits: a `<video>` whose `play()` has to run inside the click
   *  that opens it, or iOS refuses sound. Default false. */
  keepMounted?: boolean;
  /** Where focus goes on open (Base UI's default: the first tabbable element). */
  initialFocus?: PopupProps["initialFocus"];
  /** Where focus returns on close (Base UI's default: the trigger, else the element
   * focused before opening). Pass the opener when it is not a `Dialog.Trigger`:
   * Safari does not focus a button on click, so "the element focused before" is
   * the body there. A function gets how the dialog was closed. */
  finalFocus?: PopupProps["finalFocus"];
}

/** Modal popup: scrim backdrop + centered panel. Focus is trapped and restored, Escape and
 * backdrop-click dismiss, and aria-labelledby/-describedby are wired from Title/Description —
 * all from Base UI's headless Dialog. */
function DialogContent({
  children,
  className,
  keepMounted = false,
  initialFocus,
  finalFocus,
}: DialogContentProps) {
  return (
    <Base.Portal keepMounted={keepMounted}>
      <Base.Backdrop className="rc-dialog-backdrop" />
      <Base.Popup
        className={cn("rc-surface", "rc-dialog", className)}
        initialFocus={initialFocus}
        finalFocus={finalFocus}
      >
        {children}
      </Base.Popup>
    </Base.Portal>
  );
}

function DialogTitle({
  className,
  ...rest
}: Omit<ComponentProps<typeof Base.Title>, "className"> & { className?: string }) {
  return <Base.Title className={cn("rc-dialog__title", className)} {...rest} />;
}

function DialogDescription({
  className,
  ...rest
}: Omit<ComponentProps<typeof Base.Description>, "className"> & { className?: string }) {
  return <Base.Description className={cn("rc-dialog__description", className)} {...rest} />;
}

/**
 * Token-styled modal Dialog on Base UI. Compound API:
 * `<Dialog.Root><Dialog.Trigger render={<Button/>} /><Dialog.Content><Dialog.Title/>…<Dialog.Close/></Dialog.Content></Dialog.Root>`.
 */
export const Dialog = {
  Root: Base.Root,
  Trigger: Base.Trigger,
  Close: Base.Close,
  Content: DialogContent,
  Title: DialogTitle,
  Description: DialogDescription,
};
